import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { userDataBySteps } from 'assets/constants';
import { setObjectPropertiesToNull } from 'utils/src/setObjectPropertiesToNull';
import { defaultStepPropertyTypeListEnum } from 'assets/constants';
import { extractQuestionKeys, getQuestionsAfterProperty, isQuestionBeforeWidgetChanged, removeQuestionKeys } from 'utils/src/resetUserDataNextWidgets';
import { checkTypeAndShowAllQuestions } from 'utils';

const initialState = {};

const questionsStep   = userDataBySteps.stepQuestions
const stepServiceType   = userDataBySteps.stepServiceType
const stepLocation   = userDataBySteps.stepLocation
const stepService   = userDataBySteps.stepService
const stepDetails   = userDataBySteps.stepDetails
const stepDateAndTime   = userDataBySteps.stepDateAndTime

const userDataSet = createAsyncThunk(
  'userData/userDataSet',
  async (action, state) => {
    const currentState = state.getState().current;
    const currentUserData = {...currentState.userData};
    const newAction = Object.assign(currentUserData, action);
    return newAction;
  }
)

const userDataHideQuestion = createAsyncThunk(
  'userData/userDataHideQuestion',
  async (action, state) => {
    const currentState = state.getState().current;
    return {
        ...currentState?.userData ?? {},
        dont_show_question: {
          ...currentState?.userData?.dont_show_question ?? {},
          ...action
        }
    }
  }
)

const userDataShowQuestion = createAsyncThunk(
  'userData/userDataShowQuestion',
  async (action, state) => {
    const currentState = state.getState().current;
    const { [action]: _, ...dont_show_question } = currentState.userData?.dont_show_question ?? {};
    return {
      ...currentState?.userData ?? {},
      dont_show_question,
    }
  }
)

const resetUserDataNextSteps = createAsyncThunk(
  'userData/resetUserDataNextSteps',
  async (_, { getState }) => {
    const currentState = getState().current;
    const { activeStep, stepStructure } = currentState.steps;
    const currentUserData = { ...currentState.userData };
    let clientStateData = { ...currentState.userData.clientStateData };
    for (let i = activeStep + 1; i <= stepStructure.length; i++) {
      const currentSelect = userDataBySteps[stepStructure[i]];

      if (currentSelect === questionsStep) {
        currentUserData[`${currentSelect}_${i}`] = null;
        currentUserData[`questions_${i}`] = null;
        currentUserData[`single_selection_questions_${i}`] = null;
      }

      if (currentSelect === stepServiceType) {
        currentUserData.clientStateData = { ...clientStateData, serviceTypeId: null, appointmentTypeId: null }
        clientStateData = { ...clientStateData, serviceTypeId: null, appointmentTypeId: null }
        currentUserData[currentSelect] = null;
        currentUserData.appointmentType = null
        currentUserData[`questions_${i}`] = null;
        currentUserData[`single_selection_questions_${i}`] = null;
      }

      if (currentSelect === stepDateAndTime) {
        currentUserData.clientStateData = { ...clientStateData, selectedDate: null, selectedSlot: null }
        clientStateData = { ...clientStateData, selectedDate: null, selectedSlot: null }
        currentUserData[currentSelect] = null;
        currentUserData[`questions_${i}`] = null;
        currentUserData[`single_selection_questions_${i}`] = null;
      }

      if (currentSelect === stepLocation) {
        currentUserData.clientStateData = { ...clientStateData, unitId: null }
        clientStateData = { ...clientStateData, unitId: null }
        currentUserData[currentSelect] = null;
        currentUserData[`questions_${i}`] = null;
        currentUserData[`single_selection_questions_${i}`] = null;
      }

      if (currentSelect === stepService) {
        currentUserData.clientStateData = { ...clientStateData, serviceId: null }
        clientStateData = { ...clientStateData, serviceId: null }
        currentUserData[currentSelect] = null;
        currentUserData[`questions_${i}`] = null;
        currentUserData[`single_selection_questions_${i}`] = null;
      }

      if (currentSelect === stepDetails) {
        currentUserData.clientStateData = { ...clientStateData, stepDetails: null }
        clientStateData = { ...clientStateData, stepDetails: null }
        currentUserData[currentSelect] = null;
        currentUserData[`questions_${i}`] = null;
        currentUserData[`single_selection_questions_${i}`] = null;
      }
    }
    return currentUserData;
  }
);

const resetUserDataNextWidgets = createAsyncThunk(
  'userData/resetUserDataNextWidgets',
  async (resetData, { getState }) => {
    const currentState = getState().current;
    const { properties, propertyId } = resetData;
    const { activeStep, stepStructure } = currentState.steps;
    const showAllQuestions = checkTypeAndShowAllQuestions(properties)
    if (showAllQuestions) return

    const questionsAfterProperty = getQuestionsAfterProperty(properties, propertyId)
    const questionsKeysAfterProperty = extractQuestionKeys(questionsAfterProperty)
    const { location: locationKey, serviceType: serviceTypeKey, appointmentType: appointmentTypeKey, services: servicesKey } = defaultStepPropertyTypeListEnum

    const currentUserData = { ...currentState.userData };
    let clientStateData = { ...currentState.userData.clientStateData };

    const currentSelect = userDataBySteps[stepStructure[activeStep]];
    if (currentSelect === questionsStep) {
      const updatedStepQuestions = removeQuestionKeys(currentUserData[`questions_${activeStep}`], questionsKeysAfterProperty)
      currentUserData[`questions_${activeStep}`] = updatedStepQuestions;
      currentUserData[`after_property_questions_on_step_${activeStep}`] = questionsAfterProperty;
    }

    if (currentSelect === stepServiceType) {
      const resetServiceTypeAppointmentType = isQuestionBeforeWidgetChanged(properties, propertyId, [serviceTypeKey, appointmentTypeKey])
      const updatedStepQuestions = removeQuestionKeys(currentUserData[`questions_${activeStep}`], questionsKeysAfterProperty)
      currentUserData[`questions_${activeStep}`] = updatedStepQuestions;
      currentUserData[`after_property_questions_on_step_${activeStep}`] = questionsAfterProperty;
      if (resetServiceTypeAppointmentType) {
        currentUserData.clientStateData = { ...clientStateData, serviceTypeId: null, appointmentTypeId: null }
        clientStateData = { ...clientStateData, serviceTypeId: null, appointmentTypeId: null }
        currentUserData[currentSelect] = null;
        currentUserData.appointmentType = null
        currentUserData[`reset_service_type_appointment_type`] = true;
      }
    }

    if (currentSelect === stepService) {
      const resetService = isQuestionBeforeWidgetChanged(properties, propertyId, [servicesKey])
      const updatedStepQuestions = removeQuestionKeys(currentUserData[`questions_${activeStep}`], questionsKeysAfterProperty)
      currentUserData[`questions_${activeStep}`] = updatedStepQuestions;
      currentUserData[`after_property_questions_on_step_${activeStep}`] = questionsAfterProperty;
      if (resetService) {
        currentUserData.clientStateData = { ...clientStateData, serviceId: null }
        clientStateData = { ...clientStateData, serviceId: null }
        currentUserData[currentSelect] = null;
        currentUserData[`reset_service`] = true;
      }
    }

    if (currentSelect === stepLocation) {
      const resetLocation = isQuestionBeforeWidgetChanged(properties, propertyId, [locationKey])
      const updatedStepQuestions = removeQuestionKeys(currentUserData[`questions_${activeStep}`], questionsKeysAfterProperty)
      currentUserData[`questions_${activeStep}`] = updatedStepQuestions;
      currentUserData[`after_property_questions_on_step_${activeStep}`] = questionsAfterProperty;
      if (resetLocation) {
        currentUserData.clientStateData = { ...clientStateData, unitId: null }
        clientStateData = { ...clientStateData, unitId: null }
        currentUserData[currentSelect] = null;
        currentUserData[`reset_location`] = true;
      }
    }
    return currentUserData;
  }
);

const { actions, reducer } = createSlice({
  name: 'userData',
  initialState: initialState,
  reducers: {
  resetAllUserData() { return initialState; },
  resetUserDataSet(state) {
    return setObjectPropertiesToNull(state, new Set(['journeyGUID', 'processGUID']));
  } ,
  resetUserDataReschedule(state) {
    return setObjectPropertiesToNull(state, new Set(['journeyGUID', 'processGUID', 'location', 'service', 'serviceType', 'appointmentType']));
  } ,
  removeClientStateDataFields(state, action) {
    const fieldsToRemove = action.payload;
    fieldsToRemove.forEach((field) => {
      delete state.clientStateData[field];
    });
  }
},
  extraReducers: (builder) => builder
    .addCase(userDataSet.fulfilled, (state, { payload }) => ({ ...state, ...payload }))
    .addCase(userDataHideQuestion.fulfilled, (state, { payload }) => ({ ...state, ...payload }))
    .addCase(userDataShowQuestion.fulfilled, (state, { payload }) => ({ ...state, ...payload }))
    .addCase(resetUserDataNextSteps.fulfilled, (state, { payload }) => ({ ...state, ...payload }))
    .addCase(resetUserDataNextWidgets.fulfilled, (state, { payload }) => ({ ...state, ...payload }))
});

export const { resetAllUserData, resetUserDataSet, removeClientStateDataFields, resetUserDataReschedule  } = actions;
export { userDataSet, resetUserDataNextSteps, resetUserDataNextWidgets, userDataHideQuestion, userDataShowQuestion }
export default reducer;

